import { useRef, useState, useEffect } from "react";
import YarnBound from "yarn-bound";
import MessageText from "./message_text";
import InputField from "./input_field";
import InputText from "./input_text";
import InputOptions from "./input_options";
import TypingIndicator from "./typing_indicator";
import { splitCommandString } from "../../command-lexer";
import SystemMessage from "../system_message";
import storyNL from "../../assets/stories/yannick_nl.yarn?url";
import storyFR from "../../assets/stories/yannick_fr.yarn?url";
import { LANGUAGE } from "../../locales";

const STORIES = {
  NL: storyNL,
  FR: storyFR,
};

const DEBUG_MODE = document.location.search.includes("debug");

const MESSAGE_OUT = "message-out";
const MESSAGE_IN = "message-in";
const MESSAGE_SYSTEM = "message-system";

const INPUT_STATE_COLLAPSED = "collapsed";
const INPUT_STATE_TEXT = "text";
const INPUT_STATE_OPTIONS_COLLAPSED = "options_collapsed";
const INPUT_STATE_OPTIONS_OPEN = "options_open";

async function wait(ms, noDebug = false) {
  ms = DEBUG_MODE && !noDebug ? 0 : ms;
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function calculateMessageTypingTime(text) {
  // Start with a minimum time of 1 second.
  let time = 1000;
  // For each character, add a bit.
  time += text.length * 10;
  // Up till a maximum of 3 seconds.
  if (time > 3000) time = 3000;
  return time;
}

function MessageScroll({ children }) {
  const ref = useRef(null);

  async function scrollToBottom(element) {
    await wait(400, true);
    element.scrollTo({
      top: element.scrollHeight,
      behavior: "smooth",
    });
  }

  useEffect(() => {
    if (ref.current === null) return;

    scrollToBottom(ref.current);
  }, [children, ref]);

  return (
    <div ref={ref} className="chat__message-scroll">
      {children}
    </div>
  );
}

export default function Chat({ allowStart }) {
  const chatContainer = useRef();
  const runnerRef = useRef();
  const firstLineRef = useRef(true);
  const [chatLog, setChatLog] = useState([]);
  const [inputState, setInputState] = useState(INPUT_STATE_COLLAPSED);
  const [inputVariable, setInputVariable] = useState();
  const [inputPrefix, setInputPrefix] = useState();
  const [options, setOptions] = useState();
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    // Set max height on component initialization
    // This assumes that the height of the chat container fills the screen on init
    // And that the screen doesn't resize afterwards
    const { height } = chatContainer.current.getBoundingClientRect();
    chatContainer.current.style.setProperty("max-height", `${height}px`);
  }, [chatContainer]);

  useEffect(() => {
    async function fetchYarn() {
      const res = await fetch(STORIES[LANGUAGE]);
      const text = await res.text();
      runnerRef.current = new YarnBound({ dialogue: text });
      firstLineRef.current = true;
      nextDialogLine();
    }
    if (allowStart) fetchYarn();
  }, [allowStart]);

  const appendChatMessage = (message) => {
    setChatLog((oldChatLog) => [...oldChatLog, message]);
  };

  const extractCharacterNameFromMarkup = (markup) => {
    const nameProperty = markup.find((prop) => prop.name === "character");
    if (!nameProperty) {
      throw new Error(`No character property found in markup: ${markup}`);
    }

    return nameProperty.properties.name;
  };

  const nextDialogLine = async (index = undefined, text = undefined) => {
    if (text) {
      appendChatMessage({
        type: MESSAGE_OUT,
        text,
      });
    }

    if (firstLineRef.current) firstLineRef.current = false;
    else runnerRef.current.advance(index);

    const result = runnerRef.current.currentResult;
    if (result === undefined) return;

    if (result instanceof YarnBound.TextResult) {
      setIsTyping(true);
      await wait(calculateMessageTypingTime(result.text));
      setIsTyping(false);
      const name = extractCharacterNameFromMarkup(result.markup);
      const text = result.text;
      let type;
      if (name === "Player") type = MESSAGE_OUT;
      else if (name === "System") type = MESSAGE_SYSTEM;
      else type = MESSAGE_IN;

      appendChatMessage({ type, text });
      requestAnimationFrame(nextDialogLine);
    } else if (result instanceof YarnBound.OptionsResult) {
      setOptions(result.options);
      setInputState(INPUT_STATE_OPTIONS_COLLAPSED);
      openOptionsAutomaticallyAfterTimeout();
    } else if (result instanceof YarnBound.CommandResult) {
      // Execute commands here.
      const [command, ...args] = splitCommandString(result.command);
      // appendChatMessage({ type: MESSAGE_SYSTEM, text: result.command });
      handleCommand(command, args);
    }
  };

  const handleCommand = async (command, args) => {
    switch (command) {
      case "textInput":
        setInputState(INPUT_STATE_TEXT);
        setInputPrefix(args[0]);
        setInputVariable(args[1]);
        break;
      case "wait":
        await wait(parseInt(args[0]) * 1000);
        requestAnimationFrame(nextDialogLine);
        break;
      default:
        // eslint-disable-next-line no-console
        console.error(`Unknown command ${command}`);
    }
  };

  const handleTextInput = (text) => {
    setInputState(INPUT_STATE_COLLAPSED);
    // Store the value of the text in the input variable.
    // eslint-disable-next-line no-console
    console.assert(inputVariable, `No input variable set`);
    const variables = runnerRef.current.runner.variables.data;
    variables[inputVariable] = text;
    nextDialogLine(undefined, text);
  };

  const handleOpenInputOptions = () => {
    setInputState(INPUT_STATE_OPTIONS_OPEN);
  };

  const openOptionsAutomaticallyAfterTimeout = async () => {
    // Get the last text message (note that this is different from getting the last message, sometimes that is a command message like <<wait 1>>).
    const messageHistory = runnerRef.current.history.slice().reverse();
    const lastTextMessage = messageHistory.find(
      (message) => message instanceof YarnBound.TextResult,
    );
    await wait(
      lastTextMessage
        ? calculateMessageTypingTime(lastTextMessage.text) * 2
        : 250,
    );
    // If the user hasn't opened the options box by themselves, open it automatically for them.
    setInputState((prevInputState) =>
      prevInputState === INPUT_STATE_OPTIONS_COLLAPSED
        ? INPUT_STATE_OPTIONS_OPEN
        : prevInputState,
    );
  };

  const handleChooseOption = (index, text) => {
    setInputState(INPUT_STATE_COLLAPSED);
    nextDialogLine(index, text);
  };

  return (
    <div className="chat" ref={chatContainer}>
      <MessageScroll>
        {chatLog.map((message, index) => {
          if (message.type === MESSAGE_SYSTEM) {
            return (
              <SystemMessage
                key={index}
                type="notAllowed"
                text={message.text}
              />
            );
          } else {
            return (
              <MessageText
                className={
                  message.type === MESSAGE_OUT
                    ? "chat__message--out"
                    : "chat__message--in"
                }
                key={index}
              >
                {message.text}
              </MessageText>
            );
          }
        })}
        {isTyping && <TypingIndicator />}
      </MessageScroll>
      <div
        className={`chat__input ${
          inputState === INPUT_STATE_OPTIONS_OPEN
            ? "chat__input--options-open"
            : ""
        }`}
      >
        {inputState === INPUT_STATE_OPTIONS_COLLAPSED && (
          <InputField onClick={handleOpenInputOptions} />
        )}
        {inputState === INPUT_STATE_COLLAPSED && <InputField />}
        {inputState === INPUT_STATE_TEXT && (
          <InputText
            onInput={handleTextInput}
            prefix={inputPrefix}
            capitalizeFirstLetter={true}
          />
        )}
        {inputState === INPUT_STATE_OPTIONS_OPEN && (
          <InputOptions
            options={options}
            onOptionSelected={handleChooseOption}
          />
        )}
      </div>
    </div>
  );
}
